
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  const navLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Flota', path: '/flota' },
    { name: 'Paquetes Boda', path: '/paquetes-boda' },
    { name: 'Vehículos en Venta', path: '/vehiculos-en-venta' },
    { name: 'Nosotros', path: '/nosotros' },
    { name: 'Contacto', path: '/contacto' }
  ];

  const isActive = (path: string) => location.pathname === path;

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  // Update cart count whenever location changes
  useEffect(() => {
    const updateCartCount = () => {
      const cartItems = JSON.parse(localStorage.getItem('luxuryCarCart') || '[]');
      setCartCount(cartItems.length);
    };

    updateCartCount();

    // Listen for storage changes
    window.addEventListener('storage', updateCartCount);
    // Listen for custom cart update event
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, [location]);

  return (
    <header className="bg-black text-white sticky top-0 z-50 shadow-lg">
      <nav className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/3320ee3fd4d72cd7f71512a98a49bd5e/9a0b6f5fd3a70498f16d84b3d4620c50.png" 
              alt="VIP Luxury Cars MKS" 
              className="h-16 w-auto object-contain"
            />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-white font-bold text-lg tracking-wide whitespace-nowrap" style={{fontFamily: "'Playfair Display', serif"}}>
                VIP Luxury Cars
              </span>
              <span className="text-amber-400 font-extrabold text-xl tracking-widest whitespace-nowrap" style={{fontFamily: "'Playfair Display', serif"}}>
                MKS
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/flota"
              className={`hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/flota') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.fleet')}
            </Link>
            <Link
              to="/paquetes-boda"
              className={`hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/paquetes-boda') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.weddings')}
            </Link>
            <Link
              to="/nosotros"
              className={`hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/nosotros') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/contacto"
              className={`hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/contacto') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.contact')}
            </Link>
            <Link
              to="/politicas"
              className={`hover:text-yellow-400 transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/politicas') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.policies')}
            </Link>

            {/* Language Switcher */}
            <div className="flex items-center space-x-2 border-l border-gray-700 pl-6">
              <button
                onClick={() => changeLanguage('es')}
                className={`px-3 py-1 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'es'
                    ? 'bg-yellow-500 text-black font-semibold'
                    : 'hover:bg-gray-800'
                }`}
              >
                ES
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-3 py-1 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'en'
                    ? 'bg-yellow-500 text-black font-semibold'
                    : 'hover:bg-gray-800'
                }`}
              >
                EN
              </button>
            </div>

            {/* Cart */}
            <Link
              to="/carrito"
              className="relative hover:text-yellow-400 transition-colors cursor-pointer"
              aria-label="Ver carrito de compras"
            >
              <i className="ri-shopping-cart-2-line text-2xl"></i>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-2xl hover:text-yellow-400 transition-colors cursor-pointer"
            aria-label="Abrir menú de navegación"
          >
            <i className={isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-4">
            <Link
              to="/"
              onClick={() => setIsMenuOpen(false)}
              className={`block hover:text-yellow-400 transition-colors cursor-pointer ${
                isActive('/') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/flota"
              onClick={() => setIsMenuOpen(false)}
              className={`block hover:text-yellow-400 transition-colors cursor-pointer ${
                isActive('/flota') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.fleet')}
            </Link>
            <Link
              to="/paquetes-boda"
              onClick={() => setIsMenuOpen(false)}
              className={`block hover:text-yellow-400 transition-colors cursor-pointer ${
                isActive('/paquetes-boda') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.weddings')}
            </Link>
            <Link
              to="/nosotros"
              onClick={() => setIsMenuOpen(false)}
              className={`block hover:text-yellow-400 transition-colors cursor-pointer ${
                isActive('/nosotros') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.about')}
            </Link>
            <Link
              to="/contacto"
              onClick={() => setIsMenuOpen(false)}
              className={`block hover:text-yellow-400 transition-colors cursor-pointer ${
                isActive('/contacto') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.contact')}
            </Link>
            <Link
              to="/politicas"
              onClick={() => setIsMenuOpen(false)}
              className={`block hover:text-yellow-400 transition-colors cursor-pointer ${
                isActive('/politicas') ? 'text-yellow-400' : ''
              }`}
            >
              {t('nav.policies')}
            </Link>

            {/* Language Switcher Mobile */}
            <div className="flex items-center space-x-2 pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  changeLanguage('es');
                  setIsMenuOpen(false);
                }}
                className={`px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'es'
                    ? 'bg-yellow-500 text-black font-semibold'
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                Español
              </button>
              <button
                onClick={() => {
                  changeLanguage('en');
                  setIsMenuOpen(false);
                }}
                className={`px-4 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                  i18n.language === 'en'
                    ? 'bg-yellow-500 text-black font-semibold'
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                English
              </button>
            </div>

            <Link
              to="/carrito"
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center space-x-2 hover:text-yellow-400 transition-colors pt-4 border-t border-gray-700 cursor-pointer"
            >
              <i className="ri-shopping-cart-2-line text-xl"></i>
              <span>{t('nav.cart')}</span>
              {cartCount > 0 && (
                <span className="bg-yellow-500 text-black text-xs font-bold rounded-full px-2 py-1">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
}
